import React, { useEffect, useRef, useState } from "react";
import {
  DollarSign,
  Percent,
  Building2,
  BadgePercent,
  Rss,
} from "lucide-react";

const STATS_BG = "/assets/images/bg2.jpeg";

const stats = [
  {
    id: 1,
    value: 24,
    prefix: "$",
    suffix: "B",
    label: "Value of Investment",
    icon: "dollar",
  },
  {
    id: 2,
    value: 100,
    suffix: "%",
    label: "Repatriation of Capital",
    icon: "percent",
  },
  {
    id: 3,
    value: 100,
    suffix: "%",
    label: "Repatriation of Profit",
    icon: "percent",
  },
  {
    id: 4,
    value: 100,
    suffix: "%",
    label: "Foreign Ownership",
    icon: "percent",
  },
  {
    id: 5,
    value: 8,
    label: "Oil & Gas Free Zones",
    icon: "building",
  },
  {
    id: 6,
    value: 0,
    suffix: "%",
    label: "Custom Duty",
    icon: "percent",
  },
  {
    id: 7,
    value: 0,
    suffix: "%",
    label: "Tax",
    icon: "percent",
  },
  {
    id: 8,
    value: 75,
    suffix: "%",
    label: "Import Duty Rebate",
    icon: "rebate",
  },
];

const iconMap = {
  dollar: DollarSign,
  percent: Percent,
  building: Building2,
  rebate: BadgePercent,
};

const useCounter = (target, start) => {
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!start) return;

    const duration = 1600;
    const stepTime = 16;
    const steps = duration / stepTime;
    const inc = target / steps;

    let cur = 0;

    const id = setInterval(() => {
      cur += inc;

      if (cur >= target) {
        setVal(target);
        clearInterval(id);
      } else {
        setVal(Math.floor(cur));
      }
    }, stepTime);

    return () => clearInterval(id);
  }, [target, start]);

  return val;
};

const StatItem = ({ stat, start }) => {
  const Icon = iconMap[stat.icon] || Percent;
  const value = useCounter(stat.value, start);

  return (
    <div className="group flex items-start gap-4">
      <div className="w-[58px] h-[58px] rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-[#ffe21f] flex-shrink-0 transition-all duration-300 group-hover:bg-[#1f9d4c] group-hover:border-[#1f9d4c] group-hover:text-white">
        <Icon size={26} strokeWidth={2.3} />
      </div>

      <div>
        <div className="flex items-start">
          <span className="text-4xl md:text-5xl font-black text-white tabular-nums leading-none">
            {stat.prefix}
            {value.toLocaleString()}
            {stat.suffix}
          </span>
        </div>

        <p className="text-white font-bold mt-2 text-[14px] md:text-[15px] leading-tight max-w-[170px]">
          {stat.label}
        </p>
      </div>
    </div>
  );
};

const Stats = () => {
  const [start, setStart] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) setStart(true);
        }),
      { threshold: 0.3 }
    );

    if (ref.current) obs.observe(ref.current);

    return () => obs.disconnect();
  }, []);

  return (
    <section
      ref={ref}
      className="relative py-20 overflow-hidden bg-[#06183f]"
    >
      {/* BACKGROUND IMAGE */}
      <img
        src={STATS_BG}
        alt="Oil and Gas Free Zones"
        className="absolute inset-0 w-full h-full object-cover object-center opacity-45"
      />

      {/* BLUE DIM OVERLAY */}
      <div className="absolute inset-0 bg-[#06183f]/95" />

      {/* SOFT BLUR */}
      <div className="absolute inset-0 backdrop-blur-[1px]" />

      {/* LIGHT GLOWS */}
      <div className="absolute -top-24 -left-24 w-[350px] h-[350px] rounded-full bg-[#1f4fff]/20 blur-3xl pointer-events-none" />

      <div className="absolute -bottom-24 -right-24 w-[350px] h-[350px] rounded-full bg-[#0b6b1d]/10 blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6">
        {/* HEADING */}
        <div className="text-center mb-14">
          <div className="flex items-center justify-center gap-4 mb-5">
            <div className="w-[58px] h-[58px] rounded-xl bg-[#ffe21f] flex items-center justify-center text-[#06183f] shadow-xl">
              <Rss size={34} />
            </div>

            <h2
              className="text-[#ffe21f] text-4xl md:text-5xl font-black leading-tight"
              style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
            >
              Oil & Gas Free Zones Overview
            </h2>
          </div>

          <p className="max-w-4xl mx-auto text-white/90 text-[16px] md:text-[18px] leading-8 font-medium">
            OGFZA provides investors with strong incentives, strategic
            industrial locations, free repatriation of capital and profit,
            foreign ownership benefits, tax relief, customs advantages, and a
            regulated environment designed to support oil and gas investment in
            Nigeria.
          </p>
        </div>

        {/* STATS */}
        <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-x-10 gap-y-10">
          {stats.map((s) => (
            <StatItem key={s.id} stat={s} start={start} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Stats;